import type { Rng } from '../../core/rng';
import type { Vec3 } from '../../geo/types';
import { SHIP_TEMPLATES, WIND_HEADING, type SailState, type ShipType } from './shipTemplates';
import { sailSwing } from './rigging';

export interface ShipRig {
  readonly sailState: SailState;
  /**
   * How far the set sails swing about their masts, in radians, towards the side its sign gives:
   * a square rig's yards turn their +Z arms aft, a sprit rig's sails swing out to +Z. 0 is square
   * or amidships.
   */
  readonly brace: number;
  /** Horizontal unit vector along which the wind blows, in the ship's frame (bow at -X). */
  readonly downwind: Vec3;
}

/** The world wind in a ship's frame: the instance rotation by `heading` about +Y undoes it. */
export function downwindInShip(heading: number): Vec3 {
  const relative = WIND_HEADING - heading;
  return [Math.cos(relative), 0, -Math.sin(relative)];
}

/**
 * Set sails swing towards the lee, the side the wind blows to, so each ship's masts share one
 * tack: yards braced 35-40 degrees, sprit sails sheeted 10-20, all of a ship's yards braced alike
 * when she spreads only some of her sails. Furled, they stay square.
 */
export function rigFor(
  ship: { readonly type: ShipType; readonly heading: number; readonly sailState: SailState },
  rng: Rng
): ShipRig {
  const downwind = downwindInShip(ship.heading);
  if (ship.sailState === 'furled') return { sailState: ship.sailState, brace: 0, downwind };
  const lee = downwind[2] < 0 ? -1 : 1;
  const swing = sailSwing(SHIP_TEMPLATES[ship.type]);
  return { sailState: ship.sailState, brace: lee * rng.range(swing.min, swing.max), downwind };
}
